import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BusinessRegistrationForm } from '@/types/businessRegistration';
import { motion } from 'framer-motion';
import { Loader2, Mail, Shield, User, Calendar, Globe2, Info, AlertCircle } from 'lucide-react';
import { PasswordStrengthMeter } from '@/components/ui/password-strength-meter';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getNames as getCountryNames } from 'country-list';
import { useTranslation } from 'react-i18next';

interface PersonalStepProps {
  formData: BusinessRegistrationForm;
  setFormData: (data: BusinessRegistrationForm) => void;
  onNext: () => void;
  isLoading?: boolean;
  errors?: { [key: string]: string };
}

export function PersonalStep({ 
  formData, 
  setFormData, 
  onNext,
  isLoading = false,
  errors = {}
}: PersonalStepProps) {
  const { t } = useTranslation('business', { keyPrefix: 'registration' });
  const [localErrors, setLocalErrors] = useState<{ [key: string]: string }>({});

  const handleUserDataChange = (field: string, value: any) => {
    // Clear local errors when user starts typing
    setLocalErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    setFormData({
      ...formData,
      userData: {
        ...formData.userData,
        [field]: value
      }
    });
  };

  const allErrors = { ...errors, ...localErrors };
  const countries = getCountryNames();

  // Render helper for error messages
  const renderError = (fieldName: string) => {
    const error = allErrors[fieldName];
    return error ? (
      <p className="text-sm text-red-500 mt-1">{error}</p>
    ) : null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="space-y-6"
    >
      {/* Header Section with Clear Purpose */}
      <div className="text-center space-y-3 pb-6 border-b border-gray-200">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-[#97BC62] rounded-full flex items-center justify-center">
          <User className="h-8 w-8 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">{t('personalInformation')}</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            {t('personalInfoDescription')}
          </p>
        </div>
      </div>

      {/* Personal Information Section */}
      <div className="space-y-6">
        {/* Email Field with Enhanced UX */}
        <div className="space-y-2">
          <Label htmlFor="email" className="flex items-center gap-1.5 text-sm font-medium">
            <Mail className="h-4 w-4 text-primary" />
            {t('emailAddress')}
            <TooltipPrimitive.Provider>
              <TooltipPrimitive.Root>
                <TooltipPrimitive.Trigger asChild>
                  <span><Info className="h-3.5 w-3.5 text-gray-400 cursor-help" /></span>
                </TooltipPrimitive.Trigger>
                <TooltipPrimitive.Content 
                  side="top" 
                  className="z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md"
                >
                  <p className="text-xs max-w-[250px]">
                    {t('emailTooltip')}
                  </p>
                </TooltipPrimitive.Content>
              </TooltipPrimitive.Root>
            </TooltipPrimitive.Provider>
          </Label>
          <div className="relative">
            <Input
              id="email"
              type="email"
              value={formData.userData.email}
              onChange={(e) => handleUserDataChange('email', e.target.value)}
              className={`pl-10 ${allErrors.email ? 'border-red-500 pr-10' : 'border-gray-300 focus:border-primary focus:ring-primary/20'}`}
              placeholder={t('emailPlaceholder')}
              disabled={isLoading}
              aria-invalid={!!allErrors.email}
              aria-describedby={allErrors.email ? 'email-error' : 'email-helper'}
            />
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            {allErrors.email && (
              <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
            )}
          </div>
          {allErrors.email ? (
            <Alert variant="destructive" className="mt-2 py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription id="email-error" className="text-sm">
                {allErrors.email === 'email_exists' 
                  ? t('emailExists')
                  : allErrors.email}
              </AlertDescription>
            </Alert>
          ) : (
            <p id="email-helper" className="text-xs text-gray-500 flex items-center gap-1">
              <Shield className="h-3 w-3" />
              {t('emailSecure')}
            </p>
          )}
        </div>

        {/* Password Field with Enhanced UX */}
        <div className="space-y-2">
          <Label htmlFor="password" className="flex items-center gap-1.5 text-sm font-medium">
            <Shield className="h-4 w-4 text-primary" /> 
            {t('password')}
            <TooltipPrimitive.Provider>
              <TooltipPrimitive.Root>
                <TooltipPrimitive.Trigger asChild>
                  <span><Info className="h-3.5 w-3.5 text-gray-400 cursor-help" /></span>
                </TooltipPrimitive.Trigger>
                <TooltipPrimitive.Content 
                  side="top" 
                  className="z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md"
                >
                  <p className="text-xs max-w-[250px]">
                    {t('passwordTooltip')}
                  </p>
                </TooltipPrimitive.Content>
              </TooltipPrimitive.Root>
            </TooltipPrimitive.Provider>
          </Label>
          <div className="relative">
            <Input
              id="password"
              type="password"
              value={formData.userData.password}
              onChange={(e) => handleUserDataChange('password', e.target.value)}
              className={`pl-10 ${allErrors.password ? 'border-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary/20'}`}
              placeholder={t('passwordPlaceholder')}
              disabled={isLoading}
              aria-invalid={!!allErrors.password}
              aria-describedby="password-strength password-error"
            />
            <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          <PasswordStrengthMeter password={formData.userData.password} />
          {renderError('password')}
        </div>

        {/* Full Name Field */}
        <div className="space-y-2">
          <Label htmlFor="name" className="flex items-center gap-1.5 text-sm font-medium">
            <User className="h-4 w-4 text-primary" />
            {t('fullName')}
          </Label>
          <Input
            id="name"
            value={formData.userData.name}
            onChange={(e) => handleUserDataChange('name', e.target.value)}
            className={`${allErrors.name ? 'border-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary/20'}`}
            placeholder={t('fullNamePlaceholder')}
            disabled={isLoading}
            aria-invalid={!!allErrors.name}
          />
          {renderError('name')}
          <p className="text-xs text-gray-500">
            {t('fullNameHelper')}
          </p>
        </div>

        {/* Nationality Field */}
        <div className="space-y-2">
          <Label htmlFor="nationality" className="flex items-center gap-1.5 text-sm font-medium">
            <Globe2 className="h-4 w-4 text-primary" />
            {t('nationality')}
          </Label>
          <Select
            value={formData.userData.nationality}
            onValueChange={(value) => handleUserDataChange('nationality', value)}
            disabled={isLoading}
          >
            <SelectTrigger className={`${allErrors.nationality ? 'border-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary/20'}`}>
              <SelectValue placeholder={t('selectNationality')} />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {countries.map((country) => (
                <SelectItem key={country} value={country}>
                  {country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {renderError('nationality')}
        </div>

        {/* Date of Birth Field */}
        <div className="space-y-2">
          <Label htmlFor="dob" className="flex items-center gap-1.5 text-sm font-medium">
            <Calendar className="h-4 w-4 text-primary" />
            {t('dateOfBirth')}
          </Label>
          <Input
            id="dob"
            type="date"
            value={formData.userData.dateOfBirth instanceof Date
              ? formData.userData.dateOfBirth.toISOString().split('T')[0]
              : ''}
            onChange={(e) => handleUserDataChange(
              'dateOfBirth', 
              e.target.value ? new Date(e.target.value) : new Date()
            )}
            className={`${allErrors.dateOfBirth ? 'border-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary/20'}`}
            disabled={isLoading}
            max={new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
            aria-invalid={!!allErrors.dateOfBirth}
          />
          {renderError('dateOfBirth')}
          <p className="text-xs text-gray-500">
            {t('dobHelper')}
          </p>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 mb-1">{t('privacySecurity')}</p>
            <p className="text-blue-800">
              {t('privacySecurityDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-end pt-6">
        <Button 
          onClick={onNext} 
          disabled={isLoading}
          className="min-w-[120px] bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 transition-all duration-200"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('validating')}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {t('continue')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          )}
        </Button>
      </div>
    </motion.div>
  );
} 